import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

// FAQ Item type
type FAQItem = {
  question: string;
  answer: string;
  category: 'ordering' | 'setup' | 'technical' | 'pricing' | 'customization' | 'support';
};

// FAQ Item Component
function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div 
      className="py-4"
      initial={false}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex justify-between items-center w-full text-left"
      >
        <span className="text-lg font-medium text-primary">{question}</span>
        <span className={`ml-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          ↓
        </span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="pt-4 text-text/70">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FAQPage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  
  // Get categories from translations
  const categories = [
    { id: 'all', name: t('faq.categories.all') },
    { id: 'ordering', name: t('faq.categories.ordering') },
    { id: 'setup', name: t('faq.categories.setup') },
    { id: 'technical', name: t('faq.categories.technical') },
    { id: 'pricing', name: t('faq.categories.pricing') },
    { id: 'customization', name: t('faq.categories.customization') },
    { id: 'support', name: t('faq.categories.support') }
  ];

  // Get FAQ items from translations
  const faqs = t('faq.items', { returnObjects: true }) as FAQItem[];
  
  const filteredFaqs = activeCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);

  return (
    <>
      <Helmet>
        <title>{t('faq.title')}</title>
        <meta 
          name="description" 
          content={t('faq.description')}
        />
        <meta name="keywords" content="conference networking, NFC keychains, event technology, networking solution FAQ" />
      </Helmet>

      <main className="pt-24 bg-background">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-6 py-4">
          <nav className="text-sm text-text/60">
            <a href="/" className="hover:text-primary">{t('faq.breadcrumb.home')}</a>
            <span className="mx-2">/</span>
            <span className="text-text">{t('faq.breadcrumb.faq')}</span>
          </nav>
        </div>

        {/* FAQ Content */}
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-6">
              {t('faq.header.title')}
            </h1>
            <p className="text-xl text-text/80">
              {t('faq.header.subtitle')}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors duration-200 
                  ${activeCategory === category.id 
                    ? 'bg-primary text-white' 
                    : 'bg-subtle/50 text-text/70 hover:bg-subtle'
                  }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* FAQ Items */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <motion.div 
              className="divide-y divide-subtle"
              initial={false}
            >
              {filteredFaqs.map((faq, index) => (
                <FAQItem 
                  key={index} 
                  question={faq.question} 
                  answer={faq.answer} 
                />
              ))}
            </motion.div>
          </div>
        </div>
      </main>
    </>
  );
} 